<script setup lang="ts">
interface RhythmItem {
  label: string;
  heading: string;
  body: string;
  highlights?: string[];
}

interface RhythmBlockData {
  eyebrow?: string;
  header: string;
  intro?: string;
  items: RhythmItem[];
}

defineProps<{
  block: RhythmBlockData;
}>();

const phaseTones = [
  {
    icon: "lucide:sunrise",
    pill: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300",
    accent: "from-amber-300 to-amber-500",
    chip: "border-amber-200/70 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200",
    dot: "bg-amber-400",
  },
  {
    icon: "lucide:sun",
    pill: "bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300",
    accent: "from-sky-300 to-sky-500",
    chip: "border-sky-200/70 bg-sky-50 text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200",
    dot: "bg-sky-400",
  },
  {
    icon: "lucide:sunset",
    pill: "bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300",
    accent: "from-rose-300 to-rose-500",
    chip: "border-rose-200/70 bg-rose-50 text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200",
    dot: "bg-rose-400",
  },
  {
    icon: "lucide:moon-star",
    pill: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300",
    accent: "from-indigo-300 to-indigo-500",
    chip: "border-indigo-200/70 bg-indigo-50 text-indigo-800 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-200",
    dot: "bg-indigo-400",
  },
] as const;
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-lime-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -left-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-amber-400/10 blur-3xl dark:bg-amber-400/10"
    />

    <div
      class="relative mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 md:px-8 md:py-12"
    >
      <header data-reveal class="mb-6 max-w-3xl md:mb-8">
        <span
          v-if="block.eyebrow"
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ block.eyebrow }}
        </span>
        <h2
          class="mt-3 font-display text-3xl italic leading-tight text-slate-900 sm:text-4xl md:text-5xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-4 font-body text-base leading-relaxed text-slate-600 sm:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <div
        data-reveal
        class="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-4"
      >
        <article
          v-for="(item, i) in block.items"
          :key="i"
          class="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:p-6 dark:border-slate-700 dark:bg-slate-800/60"
        >
          <div
            aria-hidden="true"
            class="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r"
            :class="phaseTones[i % 4].accent"
          />

          <div class="flex items-center gap-3">
            <span
              class="flex size-10 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
              :class="phaseTones[i % 4].pill"
            >
              <Icon :name="phaseTones[i % 4].icon" class="size-5" aria-hidden="true" />
            </span>
            <span
              class="font-body text-[11px] uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400"
            >
              {{ item.label }}
            </span>
          </div>

          <h3
            class="mt-4 font-display text-xl italic leading-tight text-slate-900 sm:text-2xl dark:text-white"
          >
            {{ item.heading }}
          </h3>
          <p
            class="mt-2 font-body text-sm leading-relaxed text-slate-600 dark:text-slate-300"
          >
            {{ item.body }}
          </p>

          <ul
            v-if="item.highlights?.length"
            class="mt-auto flex flex-wrap gap-1.5 pt-5"
          >
            <li
              v-for="chip in item.highlights"
              :key="chip"
              class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-body text-[11px] font-medium tracking-wide"
              :class="phaseTones[i % 4].chip"
            >
              <span
                aria-hidden="true"
                class="size-1.5 rounded-full"
                :class="phaseTones[i % 4].dot"
              />
              {{ chip }}
            </li>
          </ul>
        </article>
      </div>
    </div>
  </section>
</template>
